import { ampli } from 'ampli';
import SendInputEditor from 'components/SendInputEditor/SendInputEditor';
import SessionRecipientPicker from 'components/SessionRecipientPicker/SessionRecipientPicker';
import { Title } from 'components/Title/Title';
import { inject, observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { uploadFileToServer } from 'services/FileUpload';
import { BugStore } from 'stores/private/BugStore';
import { ModalStore } from 'stores/private/ModalStore';
import { ProjectStore } from 'stores/private/ProjectStore';
import './ComposeMessageModal.scss';

interface ComposeMessageModalProps {
  bugStore?: BugStore;
  projectStore?: ProjectStore;
  modalStore?: ModalStore;
}

const ComposeMessageModal = ({
  bugStore,
  modalStore,
  projectStore,
}: ComposeMessageModalProps) => {
  const attachmentsRef = useRef();
  const [attachments, setAttachments] = useState([] as File[]);
  const [isSending, setIsSending] = useState(false);
  const [session, setSession] = useState(undefined as any);
  const sessionRef = useRef(undefined as any);
  sessionRef.current = session;
  const navigate = useNavigate();

  const sendComment = async (json: any) => {
    const comment = json;
    if (!comment || comment.length === 0) {
      return;
    }

    const selectedSession = sessionRef.current;
    if (!selectedSession?.id || selectedSession.id.length === 0) {
      return;
    }

    setIsSending(true);

    // Create new conversation.
    const bug = await projectStore?.createConversation(selectedSession.id);
    if (bug) {
      // Upload attachments.
      var uploadedAttachments = [] as any[];
      if (attachments) {
        for (let i = 0; i < attachments.length; i++) {
          const file = attachments[i];
          const uploadedAttachment = await uploadFileToServer(
            file,
            `${bug.id}/attachments`,
          );
          if (uploadedAttachment) {
            uploadedAttachments.push({
              url: uploadedAttachment,
              name: file.name,
              type: file.type,
            });
          }
        }
      }

      // Send comment.
      await bugStore!.addCommentToBug(
        bug.id,
        comment,
        false,
        uploadedAttachments,
      );

      if (attachmentsRef && attachmentsRef.current) {
        (attachmentsRef.current as any).clearFiles();
      }

      ampli.composeNewMessage();

      setAttachments([]);

      setIsSending(false);

      // Close modal.
      modalStore!.closeModal();

      // Navigate to conversation.
      navigate(
        `/projects/${projectStore?.currentProject?.id}/inquiries/${bug.shareToken}`,
      );
    } else {
      toast.error('Failed to create conversation.');
    }
  };

  return (
    <div className="compose-message-modal">
      <Title label="Start a conversation" className="mb-20" />
      <div className="compose-message-container">
        <SessionRecipientPicker
          session={session}
          initialSessionId={modalStore?.customData?.sessionId}
          onChange={(selectedSession) => {
            setSession(selectedSession);
          }}
        />
        <SendInputEditor
          isSending={isSending}
          inputTypes={['Reply']}
          currentInputType={'Reply'}
          onFilesSelected={(files) => {
            setAttachments([...files, ...attachments]);
          }}
          attachmentsUpdated={(files) => {
            setAttachments([...files]);
          }}
          attachmentsRef={attachmentsRef}
          attachments={attachments}
          onClickSend={sendComment}
          showAttachments={true}
          inputContentChanged={(json) => { }}
          hideTabButtons={true}
          inputPlaceholder={'Write a message...'}
          mentions={
            projectStore?.currentProjectUsers
              ? projectStore?.currentProjectUsers.map((userItem) => {
                return {
                  label: `${userItem.firstName} ${userItem.lastName}`,
                  id: userItem.id,
                  email: userItem.email,
                  profileImageUrl: userItem.profileImageUrl,
                };
              })
              : []
          }
        />
      </div>
    </div>
  );
};

export default inject(
  'bugStore',
  'modalStore',
  'projectStore',
)(observer(ComposeMessageModal));
